import { Icon } from "@single-js/common/components/icon/icon";
import { IconTip } from "@single-js/common/components/icon/icon-tip";
import { ERROR_NODE, LOADING_CELL_NODE, } from "@single-js/common/components/render-loading-error/render-loading-error";
import { renderMultipleLoadingError } from "@single-js/common/components/render-loading-error/render-multiple-loading-error";
import { FakeLinkCell } from "@single-js/common/components/super-table/super-table";
import { COLORS } from "@single-js/common/sui/color-map";
import { LEError, LELoading, LESuccess, } from "@single-js/common/util/loading-error";
import NumberFormatter from "@single-js/common/util/number-formatter";
import { LONG_EM_DASH } from "@single-js/common/util/symbols";
import BigNumber from "@single-js/common/vendor/bignumber.js/bignumber";
import { formatNodeAddress, getPartitionName, getPartitionType, } from "data/models";
import { getPartitionMasterInstance, getPartitionReplicaInstance, isPartitionImpacted, } from "data/models/cluster-metadata";
import qualifiedName from "memsql/qualified-name";
import * as React from "react";
import calculateCompressionRatio, { COMPRESSION_RATIO_TIP, } from "util/compression-ratio";
import * as logging from "util/logging";
import { buildRankDictionary } from "util/rank-dictionary";
import showDefinitionColumn from "util/show-definition-column";
import ConsoleQueryButton from "view/common/console-query-button";
import InternalLink from "view/common/internal-link";
import { displayPartitionStatus, formatPartitionInstanceRole, formatPartitionInstanceState, formatPartitionStatus, } from "view/common/models/cluster-metadata";
import { formatDatabaseStatus, getColumnIcon } from "view/common/models/schema";
import { SizeCircleCell } from "view/common/size-circle-cell";
import { ROW_COUNT_TIP } from "view/schema/const";
import "./schema-column-info.scss";
export const DATABASE_COLUMNS = [
    {
        id: "databaseName",
        title: "Name",
        formatter: ({ database }) => {
            let icon;
            if (database.databaseName === "information_schema") {
                icon = "information-schema";
            }
            else {
                icon = "database";
            }
            return (React.createElement(React.Fragment, null,
                React.createElement(Icon, { icon: icon, size: "sm", className: "entity-name-icon" }),
                React.createElement(FakeLinkCell, null, database.databaseName)));
        },
        getValue: ({ database }) => database.databaseName,
    },
    {
        id: "databaseStatus",
        title: "Status",
        formatter: formatDatabaseStatus,
        getValue: (database) => {
            return displayPartitionStatus(database.derived.status, database.derived.impacted);
        },
    },
    {
        id: "tableCount",
        title: "Table Count",
        formatter: ({ database }) => {
            if (database.databaseName === "information_schema") {
                return LONG_EM_DASH;
            }
            return database.tableCount;
        },
        getValue: ({ database }) => {
            if (database.databaseName === "information_schema") {
                return Number.NEGATIVE_INFINITY;
            }
            return database.tableCount;
        },
    },
    {
        id: "partitionCount",
        title: "Partition Count",
        formatter: ({ database }) => {
            if (database.databaseName === "information_schema") {
                return LONG_EM_DASH;
            }
            if (database.statistics &&
                database.statistics.partitionCount.isSuccess()) {
                return database.statistics.partitionCount.value.toString();
            }
            else if (database.statistics &&
                database.statistics.partitionCount.isError()) {
                return ERROR_NODE;
            }
            else {
                return LOADING_CELL_NODE;
            }
        },
        getValue: ({ database }) => {
            if (database.databaseName === "information_schema") {
                return Number.NEGATIVE_INFINITY;
            }
            // PLAT-3189
            let partitionCount = undefined;
            if (database.statistics &&
                database.statistics.partitionCount.isSuccess()) {
                partitionCount = database.statistics.partitionCount.value.toNumber();
            }
            return partitionCount;
        },
    },
    {
        id: "memoryUsage",
        title: "Memory Usage",
        formatter: ({ database, derived }) => {
            if (database.databaseName === "information_schema") {
                return LONG_EM_DASH;
            }
            if (derived.memoryUsagePercent &&
                derived.memoryUsagePercent.isSuccess()) {
                return (React.createElement(SizeCircleCell, { text: NumberFormatter.formatBytes(derived.memoryUsage), value: derived.memoryUsagePercent.value, color: COLORS["base-purple-600"] }));
            }
            else if (derived.memoryUsagePercent.isError()) {
                return ERROR_NODE;
            }
            else {
                return LOADING_CELL_NODE;
            }
        },
        getValue: ({ database, derived }) => {
            if (database.databaseName === "information_schema") {
                return Number.NEGATIVE_INFINITY;
            }
            return derived.memoryUsage.toNumber();
        },
    },
    {
        id: "diskUsage",
        title: "Disk Usage",
        formatter: ({ database, derived }) => {
            if (database.databaseName === "information_schema") {
                return LONG_EM_DASH;
            }
            if (derived.diskUsage.isSuccess() &&
                derived.diskUsagePercent.isSuccess()) {
                return (React.createElement(SizeCircleCell, { text: NumberFormatter.formatBytes(derived.diskUsage.value), value: derived.diskUsagePercent.value, color: COLORS["base-indigo-600"] }));
            }
            else if (derived.diskUsage.isError() ||
                derived.diskUsagePercent.isError()) {
                return ERROR_NODE;
            }
            else {
                return LOADING_CELL_NODE;
            }
        },
        getValue: ({ database, derived }) => {
            if (database.databaseName === "information_schema") {
                return Number.NEGATIVE_INFINITY;
            }
            if (!derived.diskUsage.isLoading() &&
                !derived.diskUsage.isError()) {
                // PLAT-3189
                return derived.diskUsage.value.toNumber();
            }
            // If loading, return undefined. If error, return null.
            return derived.diskUsage.isLoading() ? undefined : null;
        },
    },
];
const getTableRowCount = (table) => {
    if (table.statistics) {
        return table.statistics.rowCount;
    }
    else {
        return undefined;
    }
};
const getTableMemoryUse = (table) => {
    if (table.statistics) {
        return table.statistics.memoryUse;
    }
    else {
        // should never happen
        return new BigNumber(0);
    }
};
export const formatTableStorage = (table) => {
    if (table.tableStorage === "INMEMORY_ROWSTORE") {
        if (table.isSharded) {
            return "Rowstore";
        }
        else {
            return "Rowstore (reference)";
        }
    }
    else if (table.tableStorage === "COLUMNSTORE") {
        if (table.isSharded) {
            return "Columnstore";
        }
        else {
            return "Columnstore (reference)";
        }
    }
    return "Unknown";
};
export const getTableLikeIcon = (tableLike) => {
    const table = tableLike;
    if (table.tableStorage === "INMEMORY_ROWSTORE") {
        return table.isSharded ? "row" : "row-reference";
    }
    else if (table.tableStorage === "COLUMNSTORE") {
        return table.isSharded ? "column" : "column-reference";
    }
    // If the table type is neither rowstore nor columnstore, then it must be a
    // View for which we show the Font Awesome `table` icon.
    return "table";
};
export const TABLE_COLUMNS = [
    {
        id: "tableName",
        title: "Name",
        formatter: ({ table }) => (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: getTableLikeIcon(table), size: "sm", className: "entity-name-icon" }),
            React.createElement(FakeLinkCell, null, table.tableName))),
        getValue: ({ table }) => table.tableName,
    },
    {
        id: "storage",
        title: "Storage",
        formatter: ({ table }) => formatTableStorage(table),
        getValue: ({ table }) => table.tableStorage,
    },
    {
        id: "rowCount",
        title: "Row Count",
        description: ROW_COUNT_TIP,
        formatter: ({ table }) => {
            const rowCount = getTableRowCount(table);
            if (rowCount === undefined) {
                return "—";
            }
            return NumberFormatter.compactInteger(rowCount);
        },
        getValue: ({ table }) => {
            const rowCount = getTableRowCount(table);
            // We use `Number.NEGATIVE_INFINITY` here to separate `0`s from `unknown`
            // row counts when users sort the table by Row Count.
            if (rowCount === undefined) {
                return Number.NEGATIVE_INFINITY;
            }
            // PLAT-3189
            return rowCount.toNumber();
        },
    },
    {
        id: "memoryUse",
        title: "Memory Use",
        formatter: ({ table, derived }) => {
            if (derived.memoryUsagePercent.isSuccess()) {
                return (React.createElement(SizeCircleCell, { text: NumberFormatter.formatBytes(getTableMemoryUse(table)), value: derived.memoryUsagePercent.value, color: COLORS["base-purple-600"] }));
            }
            else if (derived.memoryUsagePercent.isError()) {
                return ERROR_NODE;
            }
            else {
                return LOADING_CELL_NODE;
            }
        },
        // PLAT-3189
        getValue: ({ table }) => getTableMemoryUse(table).toNumber(),
    },
    {
        id: "diskUsage",
        title: "Disk Usage",
        formatter: ({ derived }) => {
            if (derived.diskUsage.isSuccess() &&
                derived.diskUsagePercent.isSuccess()) {
                return (React.createElement(SizeCircleCell, { text: NumberFormatter.formatBytes(derived.diskUsage.value), value: derived.diskUsagePercent.value, color: COLORS["base-indigo-600"] }));
            }
            else if (derived.diskUsage.isError() ||
                derived.diskUsagePercent.isError()) {
                return ERROR_NODE;
            }
            else {
                return LOADING_CELL_NODE;
            }
        },
        getValue: ({ derived }) => {
            if (!derived.diskUsage.isLoading() &&
                !derived.diskUsage.isError()) {
                // PLAT-3189
                return derived.diskUsage.value.toNumber();
            }
            // If loading, return undefined. If error, return null.
            return derived.diskUsage.isLoading() ? undefined : null;
        },
    },
    {
        id: "tableCompression",
        title: "Table Compression",
        description: COMPRESSION_RATIO_TIP,
        formatter: ({ derived: { diskUsage, uncompressedSize }, }) => {
            return renderMultipleLoadingError([diskUsage, uncompressedSize], ([diskUsageVal, uncompressedSizeVal]) => {
                const compressionRatio = calculateCompressionRatio(diskUsageVal, uncompressedSizeVal);
                if (compressionRatio === "invalid") {
                    return LONG_EM_DASH;
                }
                else {
                    return NumberFormatter.formatPercent(compressionRatio);
                }
            });
        },
        getValue: ({ derived: { diskUsage, uncompressedSize }, }) => {
            if (diskUsage.isSuccess() && uncompressedSize.isSuccess()) {
                const compressionRate = calculateCompressionRatio(diskUsage.value, uncompressedSize.value);
                return compressionRate === "invalid" ? null : compressionRate;
            }
            // If loading, return undefined. If error, return null.
            return diskUsage.isLoading() ? undefined : null;
        },
    },
];
export const VIEW_COLUMNS = [
    {
        id: "viewName",
        title: "Name",
        formatter: ({ view }) => (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: getTableLikeIcon(view), size: "sm", className: "entity-name-icon" }),
            React.createElement(FakeLinkCell, null, view.tableName))),
        getValue: ({ view }) => view.tableName,
    },
    {
        id: "numColumns",
        title: "Number of Columns",
        formatter: ({ derived }) => derived.columnCount,
        getValue: ({ derived }) => derived.columnCount,
    },
];
// `getColumnCompressionRatio` returns:
// * "invalid" if either the compressed size or the uncompressed size are 0 (empty CS table or RS table)
// * `undefined` if either the compressed size or uncompressed size are in the loading (`undefined`) state
// * "error" if either the compressed size or the uncompressed size are in the error ("error") state
// * a `number` corresponding to the compression ratio, otherwise
export const getColumnCompressionRatio = (column) => {
    if (column.statistics) {
        if (column.statistics.compressedSize.isLoading() ||
            column.statistics.uncompressedSize.isLoading()) {
            return new LELoading();
        }
        else if (column.statistics.compressedSize.isError() ||
            column.statistics.uncompressedSize.isError()) {
            return new LEError();
        }
        else {
            const compressionRatio = calculateCompressionRatio(column.statistics.compressedSize.value, column.statistics.uncompressedSize.value);
            if (compressionRatio === "invalid") {
                return "invalid";
            }
            else {
                return new LESuccess(compressionRatio);
            }
        }
    }
    return new LELoading();
};
const getColumnMemoryUse = (column) => column.statistics ? column.statistics.memoryUse : new LELoading();
const getColumnDiskUsage = (column) => column.statistics ? column.statistics.compressedSize : new LELoading();
const EXCEPTION_TYPES = [
    "varchar",
    "varbinary",
    "json",
    "bson",
    "geo",
    "geography",
    "vector",
    "longtext",
    "mediumtext",
    "text",
    "tinytext",
];
export const COLUMN_COLUMNS = [
    {
        id: "columnName",
        title: "Name",
        formatter: ({ column }) => (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: getColumnIcon(column), iconType: "regular", size: "sm", className: "entity-name-icon" }),
            column.columnName)),
        getValue: ({ column }) => column.columnName,
    },
    {
        id: "dataType",
        title: "Data Type",
        formatter: ({ column }) => column.subType,
        getValue: ({ column }) => column.subType,
    },
    {
        id: "computed",
        title: "Computed",
        formatter: ({ column }) => column.computed ? "Yes" : "No",
        getValue: ({ column }) => column.computed,
    },
    {
        id: "memoryUsage",
        title: "Memory Usage",
        formatter: ({ column, derived }) => {
            const memoryUse = getColumnMemoryUse(column);
            if (memoryUse.isLoading() ||
                derived.memoryUsagePercent.isLoading()) {
                return LOADING_CELL_NODE;
            }
            if (memoryUse.isError() || derived.memoryUsagePercent.isError()) {
                return ERROR_NODE;
            }
            if (EXCEPTION_TYPES.includes(column.dataType)) {
                return (React.createElement(React.Fragment, null,
                    LONG_EM_DASH,
                    React.createElement(IconTip, { tipProps: { direction: "e" }, iconProps: { leftMargin: true } }, "Memory Usage calculation is not available for this data type.")));
            }
            else {
                return (React.createElement(SizeCircleCell, { text: NumberFormatter.formatBytes(memoryUse.value), value: derived.memoryUsagePercent.value, color: COLORS["base-purple-600"] }));
            }
        },
        getValue: ({ column }) => {
            const memoryUse = getColumnMemoryUse(column);
            if (memoryUse === undefined || !memoryUse.isSuccess()) {
                return Number.NEGATIVE_INFINITY;
            }
            else {
                // PLAT-3189
                return memoryUse.value.toNumber();
            }
        },
    },
    {
        id: "diskUsage",
        title: "Disk Usage",
        formatter: ({ column, derived }) => {
            const diskUsage = getColumnDiskUsage(column);
            if (diskUsage.isLoading() || derived.diskUsagePercent.isLoading()) {
                return LOADING_CELL_NODE;
            }
            if (diskUsage.isError() || derived.diskUsagePercent.isError()) {
                return ERROR_NODE;
            }
            return (React.createElement(SizeCircleCell, { text: NumberFormatter.formatBytes(diskUsage.value), value: derived.diskUsagePercent.value, color: COLORS["base-indigo-600"] }));
        },
        getValue: ({ column }) => {
            const diskUsage = getColumnDiskUsage(column);
            if (!diskUsage.isLoading() && !diskUsage.isError()) {
                // PLAT-3189
                return diskUsage.value.toNumber();
            }
            // If loading, return undefined. If error, return null.
            return diskUsage.isLoading() ? undefined : null;
        },
    },
    {
        id: "compressionRatio",
        title: "Compression Ratio",
        description: COMPRESSION_RATIO_TIP,
        formatter: ({ column }) => {
            const compressionRatio = getColumnCompressionRatio(column);
            if (compressionRatio === "invalid") {
                return LONG_EM_DASH;
            }
            else if (compressionRatio.isSuccess()) {
                return NumberFormatter.formatPercent(compressionRatio.value);
            }
            else if (compressionRatio.isError()) {
                return ERROR_NODE;
            }
            else {
                return LOADING_CELL_NODE;
            }
        },
        getValue: ({ column }) => {
            const ratio = getColumnCompressionRatio(column);
            if (ratio === "invalid") {
                return "invalid";
            }
            else {
                // If loading, return undefined. If error, return null.
                if (ratio.isSuccess()) {
                    return ratio.value;
                }
                else if (ratio.isLoading()) {
                    return undefined;
                }
                else {
                    return null;
                }
            }
        },
    },
    {
        id: "default",
        title: "Default",
        formatter: ({ column }) => {
            if (column.defaultValueSQL) {
                return (React.createElement("div", { className: "monospaced" }, column.defaultValueSQL));
            }
            else {
                return "—";
            }
        },
        getValue: ({ column }) => column.defaultValueSQL,
    },
];
export const VIEW_COLUMN_COLUMNS = [
    {
        id: "columnName",
        title: "Name",
        formatter: (column) => (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: getColumnIcon(column), iconType: "regular", size: "sm", className: "entity-name-icon" }),
            column.columnName)),
        getValue: (column) => column.columnName,
    },
    {
        id: "dataType",
        title: "Data Type",
        formatter: (column) => column.subType,
        getValue: (column) => column.subType,
    },
];
const humanizeIndexType = ({ indexName, indexType, unique }) => {
    const type = indexType;
    const name = indexName;
    if (type === "CLUSTERED COLUMN") {
        return "Columnstore";
    }
    else if (type === "BTREE" && name === "PRIMARY") {
        return "Primary Key";
    }
    else if (type === "HASH" && name === "PRIMARY") {
        return "Primary Key (Hash)";
    }
    else if (type === "HASH" && unique) {
        return "Unique Key (Hash)";
    }
    else if (unique) {
        return "Unique Key";
    }
    else if (type === "BTREE") {
        return "Key";
    }
    else if (type === "SHARD") {
        return "Shard";
    }
    else if (type === "HASH") {
        return "Hash";
    }
    else if (type === "FULLTEXT") {
        return "Fulltext";
    }
    else if (type === "COLUMNSTORE HASH") {
        return "Columnstore Hash";
    }
    return type;
};
export const INDEX_COLUMNS = [
    {
        id: "indexName",
        title: "Name",
        formatter: (index) => (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: "index", size: "sm", className: "entity-name-icon" }),
            index.indexName)),
        getValue: (index) => index.indexName,
    },
    {
        id: "memoryUsage",
        title: "Memory Usage",
        formatter: (index) => NumberFormatter.formatBytes(index.memoryUse),
        // PLAT-3189
        getValue: (index) => index.memoryUse.toNumber(),
    },
    {
        id: "diskUse",
        title: "Disk Usage",
        formatter: (index) => {
            if (index.diskUse === undefined) {
                return LONG_EM_DASH;
            }
            else {
                return NumberFormatter.formatBytes(index.diskUse);
            }
        },
        getValue: (index) => {
            if (index.diskUse === undefined) {
                return undefined;
            }
            else {
                return index.diskUse.toNumber();
            }
        },
    },
    {
        id: "indexType",
        title: "Type",
        formatter: (index) => humanizeIndexType(index),
        getValue: (index) => humanizeIndexType(index),
    },
    {
        id: "indexColumns",
        title: "Columns",
        formatter: (index) => index.columnNames.join(", "),
        getValue: (index) => index.columnNames.join(", "),
    },
    {
        id: "indexUnique",
        title: "Unique",
        formatter: (index) => (index.unique ? "Yes" : "No"),
        getValue: (index) => index.unique,
    },
];
export const STORED_PROCEDURE_COLUMNS = [
    {
        id: "name",
        title: "Name",
        formatter: (sp) => (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: "stored-procedure", size: "sm", className: "entity-name-icon" }),
            sp.name)),
        getValue: (sp) => sp.name,
    },
    {
        id: "definer",
        title: "Definer",
        formatter: (sp) => sp.definer,
        getValue: (sp) => sp.definer,
    },
    showDefinitionColumn({
        formatter: ({ databaseName, name }) => (React.createElement(ConsoleQueryButton, { tipProps: {
                direction: "w",
                tooltip: "Get the SHOW CREATE PROCEDURE output for this procedure.",
            }, query: `SHOW CREATE PROCEDURE ${qualifiedName({
                databaseName,
                name,
            })} \\G`, category: "schema-explorer", action: "show-create-function" })),
    }),
];
export const UDF_COLUMNS = [
    {
        id: "name",
        title: "Name",
        formatter: (udf) => (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: "udf", size: "sm", className: "entity-name-icon" }),
            udf.name)),
        getValue: (udf) => udf.name,
    },
    {
        id: "type",
        title: "Type",
        formatter: (udf) => {
            switch (udf.type) {
                case "SCALAR_VALUED_FUNCTION":
                    return "Scalar-Valued";
                case "TABLE_VALUED_FUNCTION":
                    return "Table-Valued";
                default:
                    return "Unknown";
            }
        },
        getValue: (udf) => udf.type,
    },
    {
        id: "definer",
        title: "Definer",
        formatter: (udf) => udf.definer,
        getValue: (udf) => udf.definer,
    },
    showDefinitionColumn({
        formatter: ({ databaseName, name }) => (React.createElement(ConsoleQueryButton, { tipProps: {
                direction: "sw",
                tooltip: "Get the SHOW CREATE FUNCTION output for this function.",
            }, query: `SHOW CREATE FUNCTION ${qualifiedName({
                databaseName,
                name,
            })} \\G`, category: "schema-explorer", action: "show-create-function" })),
    }),
];
export const UDA_COLUMNS = [
    {
        id: "name",
        title: "Name",
        formatter: (uda) => (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: "aggregate", size: "sm", className: "entity-name-icon" }),
            uda.name)),
        getValue: (udf) => udf.name,
    },
    showDefinitionColumn({
        formatter: ({ databaseName, name }) => (React.createElement(ConsoleQueryButton, { tipProps: {
                direction: "sw",
                tooltip: "Get the SHOW CREATE AGGREGATE output for this aggregate.",
            }, query: `SHOW CREATE AGGREGATE ${qualifiedName({
                databaseName,
                name,
            })} \\G`, category: "schema-explorer", action: "show-create-function" })),
    }),
];
export const PARTITION_COLUMNS = [
    {
        id: "databaseName",
        title: "Partition",
        formatter: (derivedPartition) => {
            const partitionName = getPartitionName(derivedPartition);
            return (React.createElement(React.Fragment, null,
                React.createElement(Icon, { icon: "chart-pie", size: "sm", className: "entity-name-icon" }),
                React.createElement("span", null, partitionName)));
        },
        getValue: (derivedPartition) => getPartitionName(derivedPartition),
    },
    {
        id: "partitionStatus",
        title: "Health Status",
        formatter: formatPartitionStatus,
        getValue: (partition) => {
            return displayPartitionStatus(partition.status, isPartitionImpacted(partition));
        },
    },
    {
        id: "numPartitionInstances",
        title: "Total Partition Instances",
        formatter: (derivedPartition) => (React.createElement(InternalLink, { routeInfo: {
                name: "cluster.databases.partition",
                params: {
                    databaseName: derivedPartition.databaseName,
                    partitionName: getPartitionName(derivedPartition),
                },
            }, category: "schema-explorer", clusterLink: true, onClick: 
            /* stop propagation to the cell click event */ (e) => e.stopPropagation() }, derivedPartition.partitionInstances.length)),
        getValue: ({ partitionInstances }) => partitionInstances.length,
    },
    {
        id: "partitionType",
        title: "Partition Type",
        formatter: (derivedPartition) => getPartitionType(derivedPartition),
        getValue: (derivedPartition) => getPartitionType(derivedPartition),
    },
    {
        id: "masterPartition",
        title: "Master Partition Instance Node",
        formatter: (derivedPartition) => {
            const masterPartitionInstance = getPartitionMasterInstance(derivedPartition);
            if (masterPartitionInstance) {
                return (React.createElement(InternalLink, { routeInfo: {
                        name: "cluster.nodes.node",
                        params: {
                            nodeAddress: formatNodeAddress(masterPartitionInstance),
                        },
                    }, category: "schema-explorer", clusterLink: true, onClick: 
                    /* stop propagation to the cell click event */ (e) => e.stopPropagation() }, formatNodeAddress(masterPartitionInstance)));
            }
            else {
                logging.log("warn", "Master Partition Instance Node does not exist.");
                return "N/A";
            }
        },
        getValue: (derivedPartition) => {
            const masterPartitionInstance = getPartitionMasterInstance(derivedPartition);
            if (masterPartitionInstance) {
                return formatNodeAddress(masterPartitionInstance);
            }
        },
    },
    {
        id: "replicaPartition",
        title: "Replica Partition Instance Node",
        formatter: (derivedPartition) => {
            const replicaPartitionInstance = getPartitionReplicaInstance(derivedPartition);
            if (replicaPartitionInstance === "all-leaf-nodes") {
                return "All Leaf Nodes";
            }
            else if (replicaPartitionInstance === undefined) {
                //This can happen if HA is not enabled
                return "N/A";
            }
            else {
                return (React.createElement(InternalLink, { routeInfo: {
                        name: "cluster.nodes.node",
                        params: {
                            nodeAddress: formatNodeAddress(replicaPartitionInstance),
                        },
                    }, category: "schema-explorer", clusterLink: true, onClick: 
                    /* stop propagation to the cell click event */ (e) => e.stopPropagation() }, formatNodeAddress(replicaPartitionInstance)));
            }
        },
        getValue: (derivedPartition) => {
            const replicaPartitionInstance = getPartitionReplicaInstance(derivedPartition);
            if (replicaPartitionInstance === "all-leaf-nodes" ||
                !replicaPartitionInstance) {
                return replicaPartitionInstance;
            }
            else {
                return formatNodeAddress(replicaPartitionInstance);
            }
        },
    },
];
export const PARTITION_INSTANCE_ROLE_COLUMN = {
    id: "partitionInstanceRole",
    title: "Role",
    formatter: (partitionInstance) => formatPartitionInstanceRole(partitionInstance.role),
    getValue: (partitionInstance) => {
        switch (partitionInstance.role) {
            case "master":
                return 3;
            case "slave":
                return 2;
            case "detached master":
                return 1;
            case "detached slave":
                return 0;
            default:
                return -1;
        }
    },
};
// In this object, we rank partition instance states so as to be able to compare
// their states from "best" to "worst"
export const RANK_PARTITION_INSTANCE_STATE = buildRankDictionary([
    "online",
    "replicating",
    "provisioning",
    "transition",
    "pending",
    "recovering",
    "unrecoverable",
    "offline",
]);
const getPartitionInstanceRank = (state) => RANK_PARTITION_INSTANCE_STATE[state];
export const PARTITION_INSTANCE_STATE_COLUMN = {
    id: "partitionInstanceState",
    title: "State",
    formatter: (partitionInstance) => formatPartitionInstanceState(partitionInstance.state),
    getValue: (partitionInstance) => getPartitionInstanceRank(partitionInstance.state),
};
export const PARTITION_INSTANCE_LOG_COLUMN = {
    id: "partitionInstancePosition",
    title: "Log Position",
    formatter: (partitionInstance) => partitionInstance.position || LONG_EM_DASH,
    getValue: (partitionInstance) => partitionInstance.position,
};
export const PARTITION_INSTANCE_COLUMNS = [
    PARTITION_INSTANCE_STATE_COLUMN,
    PARTITION_INSTANCE_ROLE_COLUMN,
    {
        id: "partitionInstanceNodeAddress",
        title: "Node Address",
        formatter: (partitionInstance) => (React.createElement(InternalLink, { routeInfo: {
                name: "cluster.nodes.node",
                params: {
                    nodeAddress: formatNodeAddress(partitionInstance),
                },
            }, category: "schema-explorer", clusterLink: true, onClick: 
            /* stop propagation to the cell click event */ (e) => e.stopPropagation() },
            partitionInstance.host,
            ":",
            partitionInstance.port.toString())),
        getValue: (partitionInstance) => `${partitionInstance.host}:${partitionInstance.port}`,
    },
    PARTITION_INSTANCE_LOG_COLUMN,
];
